import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getVirtualSession, createVirtualSession, deleteVirtualSession, getStatus, getPortfolio, getActiveStrategyPublic, getReportSettings, updateReportSettings } from '../../api';

import { ProfileCard, PasswordCard } from '../account/ProfileCards';

const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

// Менеджер 13.10 (сторінка «Налаштування»): прибрано три KPI-картки згори
// (дублювали головну) і два адмінські блоки — «лише marginable» та «Кредитне
// плече»: тепер це одне значення на всі акаунти, адмінка → «Налаштування
// акаунтів». Поки його там не збережено, діють значення з trading_settings.
// Код лишено за прапорцями — відкат = true.
const SHOW_ACCOUNT_KPI_CARDS        = false;
const SHOW_PER_USER_MARGIN_CONTROLS = false;

/* ── Palette (Figma 303-35629) ─────────────────────────────────────── */
const C = {
  bg:        '#F9FAFB',
  card:      '#FFFFFF',
  border:    '#E5E7EB',
  textMain:  '#1A1A1A',
  textDim:   '#5B646F',
  textSub:   '#686A7B',
  primary:   '#475FFA',
  dark:      '#101220',
  success:   '#00A656',
  danger:    '#ED4B82',
  shadow:    '0px 2px 6px 0px rgba(0,0,0,0.04), 0px 0px 0px 1px rgba(0,0,0,0.06)',
};

/* ── Icons ─────────────────────────────────────────────────────────── */
// Банер «запит уже надіслано»: секунди до дозволеного повторного запиту
// рахуються від дедлайну, тож число йде вниз, а на нулі текст міняється.
function pendingRetryText(until, now) {
  const left = Math.max(0, Math.ceil((until - now) / 1000));
  return left > 0
    ? `📲 Запит на вхід уже надіслано — підтвердіть його у застосунку IBKR (IB Key). Якщо пуш так і не прийде, новий запит можна надіслати через ${left} с.`
    : '📲 Запит на вхід уже надіслано — підтвердіть його у застосунку IBKR (IB Key). Якщо пуш так і не прийшов, натисніть «Перепідключити» ще раз.';
}

function WalletIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="2" y="5" width="16" height="12" rx="2" stroke="#1C69E3" strokeWidth="1.5"/>
      <path d="M2 8h16" stroke="#1C69E3" strokeWidth="1.5"/>
      <circle cx="14" cy="12" r="1.5" fill="#1C69E3"/>
    </svg>
  );
}
function TrendIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <polyline points="2,14 7,8 11,11 18,4" stroke="#00A656" strokeWidth="1.5" fill="none"/>
      <polyline points="13,4 18,4 18,9"     stroke="#00A656" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 2l7 3v5c0 4-3 7-7 8C7 17 4 14 4 10V5l6-3z" stroke="#9799A4" strokeWidth="1.5" fill="none"/>
      <path d="M7 10l2 2 4-4" stroke="#9799A4" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function IBKRIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="1.5"/>
      <path d="M8 12h8M12 8l4 4-4 4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function RadioUnchecked() {
  return (
    <div style={{
      width: 20, height: 20, borderRadius: 10, border: '1.5px solid #D1D5DC',
      background: '#fff', flexShrink: 0,
    }} />
  );
}
function RadioChecked() {
  return (
    <div style={{
      width: 20, height: 20, borderRadius: 10, border: '1.5px solid #475FFA',
      background: '#fff', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ width: 10, height: 10, borderRadius: 5, background: '#475FFA' }} />
    </div>
  );
}

/* ── KPI stat card ─────────────────────────────────────────────────── */
function StatCard({ label, value, icon, iconBg, sub, subColor, valueColor }) {
  return (
    <div style={{
      background: C.card,
      border: `1px solid rgba(224,229,235,0.5)`,
      borderRadius: 20,
      padding: 24,
      boxShadow: '0px 2px 6px 0px rgba(0,0,0,0.04), 0px 0px 0px 1px rgba(0,0,0,0.06)',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      minHeight: 160,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: C.textDim }}>{label}</div>
        <div style={{
          width: 38, height: 38, borderRadius: 20,
          background: iconBg || 'rgba(28,105,227,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </div>
      </div>
      <div>
        <div style={{
          fontSize: 28, fontWeight: 600, color: valueColor || C.textMain,
          lineHeight: 1.35, letterSpacing: '-0.027em',
        }}>
          {value}
        </div>
        {sub && (
          <div style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              padding: '4px 12px', borderRadius: 9999,
              background: subColor || '#F3F4F6',
              color: '#1E2939',
              fontSize: 12,
            }}>
              {sub}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}


const WEEKDAYS = ['Понеділок', 'Вівторок', 'Середа', 'Четвер', 'Пʼятниця', 'Субота', 'Неділя'];
const HOURS    = Array.from({ length: 24 }, (_, h) => h);
const MINUTES  = Array.from({ length: 12 }, (_, i) => i * 5);
const pad2     = (n) => String(n).padStart(2, '0');
const SELECT_STYLE = { padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, cursor: 'pointer', background: C.card, color: C.textMain };

/* ── IBKR Connection Card ─────────────────────────────────────────── */
function IbkrCard({ broker, onRefresh, accountType, unsettledCash, ibkrNetLiq, marginableOnly,
                    marginLeverage, marginLeverageCap, marginLeverageCeiling, marginLeverageRungs,
                    marginBorrowed, marginInterest,
                    twofaNotifyStartHour, twofaNotifyEndHour,
                    twofaReloginWeekday, twofaReloginHour, twofaReloginMinute,
                    needs2fa, containers, isAdmin, roleKnown }) {
  const [showForm,  setShowForm]  = useState(false);
  const [login,     setLogin]     = useState('');
  const [password,  setPassword]  = useState('');
  const [accountId, setAccountId] = useState('');
  // Режим підключення. Для звичайних юзерів завжди 'live' — вони підключають
  // бойовий акаунт, і селектора не бачать. Адміну потрібен ще й ПАПЕРОВИЙ:
  // на ньому працює технічний акаунт нічної докачки даних. Значення керує
  // не лише написом — від нього залежить TRADING_MODE шлюза і порт
  // (4003/4001 live, 4004/4002 paper), тож помилка тут = логін у бойовий
  // шлюз паперовими даними: 2FA-пуш і відмова брокера.
  const [mode,      setMode]      = useState(broker?.trading_mode === 'paper' ? 'paper' : 'live');
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState(null);
  const [mgOnly,    setMgOnly]    = useState(!!marginableOnly);
  const [mgSaving,  setMgSaving]  = useState(false);
  const [lev,        setLev]        = useState(!!marginLeverage);
  const [levSaving,  setLevSaving]  = useState(false);
  const [pendingCap, setPendingCap] = useState(null);   // rung awaiting risk confirm
  // Розклад тижневого підтвердження входу IBKR (2FA). День і час планового
  // запиту (дефолт пн 10:00) + вікно годин, у якому шлемо повторні запити,
  // поки вхід не підтверджено (дефолт 10:00–20:00). Усе за київським часом.
  const TWOFA_DEF = { weekday: 0, hour: 10, minute: 0, start: 10, end: 20 };
  const twofaFromProps = () => ({
    weekday: twofaReloginWeekday ?? TWOFA_DEF.weekday,
    hour:    twofaReloginHour    ?? TWOFA_DEF.hour,
    minute:  twofaReloginMinute  ?? TWOFA_DEF.minute,
    start:   twofaNotifyStartHour ?? TWOFA_DEF.start,
    end:     twofaNotifyEndHour   ?? TWOFA_DEF.end,
  });
  const [twofa, setTwofa] = useState(twofaFromProps);
  const [notifySaving, setNotifySaving] = useState(false);

  useEffect(() => { setMgOnly(!!marginableOnly); }, [marginableOnly]);
  useEffect(() => { setLev(!!marginLeverage); }, [marginLeverage]);
  useEffect(() => { setTwofa(twofaFromProps()); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [twofaReloginWeekday, twofaReloginHour, twofaReloginMinute, twofaNotifyStartHour, twofaNotifyEndHour]);

  // ── Connection state (3-way, honest) ──────────────────────────────────────
  // GROUND TRUTH = is_verified: the backend probe sets it ONLY after a REAL login
  // (managedAccounts readable), never optimistically. A running bot also proves a
  // live session. needs2fa (watchdog log-scan) is a NOISY hint — it can stick
  // "true" even right after a successful login — so it must NOT override a
  // probe-confirmed is_verified; it only shapes the "connecting" wording below.
  const hasCreds   = !!(broker && broker.ibkr_login);
  const gwRunning  = containers?.ibgw?.status === 'running';
  const botRunning = containers?.bot?.status === 'running';
  const awaiting2fa = !!needs2fa;
  // Обрив, записаний ботом або watchdog (bot/link_state.py → profile.broker.link).
  // Без цього «Підключено» трималось, поки живий контейнер бота, хоч би що було
  // зі звʼязком. Короткі розриви (до grace_s) не показуємо — нічний рестарт шлюзу.
  const link       = broker?.link;
  const linkLost   = hasCreds && link?.state === 'lost'
                     && (link.lost_for_s ?? 0) >= (link.grace_s ?? 60);
  const connected  = hasCreds && !linkLost
                     && ((!!broker.is_verified && gwRunning) || botRunning);
  const connecting = hasCreds && !connected && !linkLost;   // creds saved, login not confirmed

  // Timer that softens the post-reconnect banner (no synchronous "connected"
  // signal exists — the gateway logs in out-of-band after 2FA approval).
  const reconnectTimer = useRef(null);
  // Auto-dismiss for one-shot success banners (e.g. "✅ Підключено") so they don't
  // linger next to the permanent connected-state block — actionable ⏳/2FA banners
  // are left up.
  const msgTimer = useRef(null);
  const flashMsg = (m, ms = 6000) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg(m);
    msgTimer.current = setTimeout(() => setMsg(null), ms);
  };
  // Тік раз на секунду, лише поки на екрані банер зі зворотним відліком.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (msg?.until == null) return undefined;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [msg?.until]);
  useEffect(() => () => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    if (msgTimer.current) clearTimeout(msgTimer.current);
  }, []);

  const headers = {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const save = async () => {
    if (!login || !password) { setMsg({ type: 'error', text: 'Заповніть логін і пароль' }); return; }
    // 🚨 2026-09-29: профіль не довантажився (кабінет узяв закешовану сторінку
    // замість /api/user/profile) → кабінет не знав, що це адмін, сховав вибір
    // Live/Paper і записав паперовому акаунту trading_mode='live'. Далі шлюз
    // шукали на бойовому порту, і кнопка запуску бота не з'являлась. Поки роль
    // невідома — не зберігаємо взагалі, а не вгадуємо режим.
    if (!roleKnown) {
      setMsg({ type: 'error', text: 'Профіль ще не завантажився — оновіть сторінку (Ctrl+Shift+R) і спробуйте ще раз.' });
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/api/user/broker-credentials`, {
        method: 'POST', headers,
        body: JSON.stringify({ ibkr_login: login, ibkr_password: password, account_id: accountId, trading_mode: isAdmin ? mode : (broker?.trading_mode || 'live') }),
      });
      const d = await r.json();
      if (r.status === 403) {
        const left = d.detail?.eligible_at ? ` (доступно з ${new Date(d.detail.eligible_at).toLocaleDateString('uk-UA')})` : '';
        setMsg({ type: 'warning', text: '🔒 Live ще заблоковано' + left });
        setSaving(false); return;
      }
      // Credentials saved — immediately start the IB Gateway so the IBKR session
      // (and 2FA) is established and can be verified BEFORE any bot runs. Keep the
      // typed values (do NOT clear the form) so nothing has to be re-entered if
      // the connection needs a retry.
      setMsg({ type: 'info', text: '⏳ Підключення до IBKR… відкрийте застосунок IBKR і підтвердіть вхід (2FA)' });
      try {
        const cr = await fetch(`${BASE}/api/broker/connect`, { method: 'POST', headers });
        const cd = await cr.json().catch(() => ({}));
        if (cr.ok && cd.ok) {
          if (cd.needs_2fa) {
            // Actionable — keep it up until the user approves 2FA (poll clears state).
            setMsg({ type: 'info', text: '⏳ Gateway запущено — підтвердіть 2FA у застосунку IBKR. Далі стане доступна кнопка запуску робота.' });
          } else {
            // One-shot success — the permanent "✅ Підключено" block already shows the
            // state, so auto-dismiss this banner instead of letting it linger.
            flashMsg({ type: 'success', text: cd.reused
              ? '✅ Підключено — акаунт IBKR уже активний (нового підтвердження 2FA не потрібно).'
              : '✅ Gateway запущено — триває підключення до IBKR. Кнопка запуску робота стане доступна, коли gateway буде готовий.' });
          }
          setShowForm(false);
        } else {
          const err = cd.detail || cd.error || `HTTP ${cr.status}`;
          setMsg({ type: 'error', text: 'Дані збережено, але підключення до IBKR не вдалось: ' + err });
        }
      } catch {
        setMsg({ type: 'error', text: 'Дані збережено, але не вдалось запустити підключення до IBKR' });
      }
      onRefresh();
    } catch {
      setMsg({ type: 'error', text: 'Помилка збереження' });
    }
    setSaving(false);
  };

  const disconnect = async () => {
    if (!window.confirm('Відключити IBKR? Робот зупиниться, зʼєднання з IBKR буде розірвано, а збережені дані входу — видалено. Ви зможете підключити цей або інший акаунт заново.')) return;
    // Variant A — FULL disconnect: this endpoint stops the bot, removes the IB
    // Gateway container AND wipes the credentials. The old DELETE /broker-credentials
    // only removed the DB row and left the gateway running (→ 2FA-push storm), so we
    // no longer call it here.
    await fetch(`${BASE}/api/broker/disconnect`, { method: 'POST', headers });
    onRefresh();
  };

  // Open the credentials form pre-filled with the account the gateway is
  // connected with (login + account_id). The password is never returned by the
  // backend, so it must be re-entered before saving.
  const openForm = () => {
    setLogin(broker?.ibkr_login || '');
    setAccountId(broker?.account_id || '');
    setPassword('');
    setMsg(null);
    setShowForm(true);
  };

  // Force a fresh IB Gateway login → new 2FA push. Needed for the weekly
  // re-login (or when a competing IBKR login bumped the session) — the plain
  // "connect" is idempotent and would NOT re-send a push if the gateway record
  // still looks alive.
  const [reconnecting, setReconnecting] = useState(false);
  const reconnect = async () => {
    setReconnecting(true);
    setMsg({ type: 'info', text: '⏳ Надсилаю запит на вхід — підтвердіть 2FA у застосунку IBKR' });
    try {
      const r = await fetch(`${BASE}/api/broker/reconnect`, { method: 'POST', headers });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok && d.pending) {
        // Повторне натискання, поки попередній вхід ще чекає підтвердження:
        // бекенд НЕ перестворює шлюз (це скасувало б пуш, що вже в телефоні).
        // Секунди до повторного запиту — зворотним відліком (msg.until), а не
        // застиглим числом з моменту натискання.
        setMsg({ type: 'info', until: Date.now() + (Number(d.retry_after) || 0) * 1000 });
      } else if (r.ok && d.ok) {
        setMsg({ type: 'success', text: '📲 Push надіслано — підтвердіть вхід у застосунку IBKR (IB Key). З’єднання встановлюється у фоні…' });
        // No synchronous "connected" signal after a manual reconnect: the gateway
        // logs in AFTER you approve 2FA, out-of-band. So don't leave a permanent
        // "waiting" banner — after the expected window, soften it to a self-
        // explanatory line and pull fresh status (the card also polls every 10s).
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(() => {
          setMsg({ type: 'success', text: '✅ Якщо ви підтвердили вхід у застосунку — з’єднання з IBKR встановлено. Статус нижче оновлюється автоматично.' });
          onRefresh();
        }, 30000);
      } else {
        setMsg({ type: 'error', text: 'Не вдалось перепідключитись: ' + (d.detail || d.error || `HTTP ${r.status}`) });
      }
    } catch {
      setMsg({ type: 'error', text: 'Не вдалось перепідключитись до IBKR' });
    }
    setReconnecting(false);
    onRefresh();
  };

  // Save a leverage multiplier. cap<=1.0 turns leverage off; a rung (1.5/1.8/2.0)
  // turns it on. Backend clamps to the admin ceiling + forces marginable-only.
  const saveCap = async (cap) => {
    setLevSaving(true);
    try {
      const r = await fetch(`${BASE}/api/user/margin-leverage`, {
        method: 'POST', headers, body: JSON.stringify({ cap }),
      });
      if (!r.ok) throw new Error();
      setLev(cap > 1.0);
      if (cap > 1.0) setMgOnly(true);   // backend forces the filter on
      setPendingCap(null);
      onRefresh();
    } catch { /* keep previous state */ }
    setLevSaving(false);
  };

  const onSelectCap = (cap) => {
    if (cap <= 1.0) { saveCap(1.0); return; }   // 1:1 — turn leverage off, no confirm
    if (lev)        { saveCap(cap); return; }   // already leveraged — switch rung directly
    setPendingCap(cap);                          // turning ON — show the risk panel first
  };

  // Зберегти розклад 2FA одразу після зміни будь-якого поля (як решта
  // перемикачів картки). Watchdog читає його наживо, без перезапуску робота.
  const saveTwofa = async (patch) => {
    const next = { ...twofa, ...patch };
    setTwofa(next);
    setNotifySaving(true);
    try {
      const r = await fetch(`${BASE}/api/user/twofa-schedule`, {
        method: 'POST', headers,
        body: JSON.stringify({
          weekday: next.weekday, hour: next.hour, minute: next.minute,
          start_hour: next.start, end_hour: next.end,
        }),
      });
      if (!r.ok) throw new Error();
      onRefresh();
    } catch {
      /* keep optimistic UI; next profile refresh reconciles */
    }
    setNotifySaving(false);
  };

  const toggleMarginableOnly = async () => {
    const next = !mgOnly;
    setMgOnly(next);           // optimistic
    setMgSaving(true);
    try {
      const r = await fetch(`${BASE}/api/user/marginable-only`, {
        method: 'POST', headers, body: JSON.stringify({ enabled: next }),
      });
      if (!r.ok) throw new Error();
      onRefresh();
    } catch {
      setMgOnly(!next);        // revert on failure
    }
    setMgSaving(false);
  };

  return (
    <div style={{
      background: C.card,
      border: `0.667px solid ${C.border}`,
      borderRadius: 20,
      padding: 24,
      boxShadow: C.shadow,
    }}>
      {/* Title */}
      <div style={{ fontSize: 20, fontWeight: 600, color: C.textMain, marginBottom: 8, lineHeight: '28px' }}>
        Interactive Brokers
      </div>
      <div style={{ fontSize: 16, color: C.textMain, lineHeight: '24px', marginBottom: 20 }}>
        Підключіть ваш Interactive Brokers акаунт щоб алгоритм торгував від вашого імені.
      </div>

      {/* Connection status — rendered at card level so it stays visible after the
          form closes (the connect result must not vanish with the form). */}
      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.type === 'success' ? '#F0FDF4' : msg.type === 'warning' ? '#FFFBEB' : msg.type === 'error' ? '#FEF2F2' : '#EFF6FF',
          color:      msg.type === 'success' ? '#15803D' : msg.type === 'warning' ? '#92400E' : msg.type === 'error' ? '#DC2626' : '#1E40AF',
          border: `1px solid ${msg.type === 'success' ? '#86EFAC' : msg.type === 'warning' ? '#FDE68A' : msg.type === 'error' ? '#FECACA' : '#BFDBFE'}`,
        }}>
          {msg.until != null ? pendingRetryText(msg.until, nowTick) : msg.text}
        </div>
      )}

      {/* Connection form — rendered near the top of the card so clicking
          "Змінити"/"Підключити" shows it immediately (previously it sat below
          all the sub-sections and appeared far down, out of view). */}
      {showForm && (
        <div style={{ marginBottom: 20, padding: 20, background: '#F8FAFC', borderRadius: 12, border: `1px solid ${C.border}` }}>
          <div style={{ fontWeight: 600, marginBottom: 16, color: C.textMain }}>Облікові дані IBKR</div>
          {[
            { label: "Логін IBKR",  value: login,     set: setLogin,     type: 'text',     placeholder: 'Ім\'я користувача' },
            { label: 'Пароль',      value: password,  set: setPassword,  type: 'password', placeholder: 'Пароль IBKR'      },
            { label: 'Account ID',  value: accountId, set: setAccountId, type: 'text',     placeholder: 'напр. U1234567'   },
          ].map(f => (
            <div key={f.label} style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: C.textDim, display: 'block', marginBottom: 4 }}>{f.label}</label>
              <input
                type={f.type}
                value={f.value}
                onChange={e => f.set(e.target.value)}
                placeholder={f.placeholder}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: `1px solid ${C.border}`, background: C.card,
                  fontSize: 14, outline: 'none', boxSizing: 'border-box', color: C.textMain,
                }}
              />
            </div>
          ))}
          {hasCreds && (
            <div style={{ fontSize: 12, color: C.textDim, marginTop: -4, marginBottom: 12 }}>
              Логін та Account ID підставлено з підключеного акаунта. Пароль у кабінеті не зберігається — введіть його ще раз, щоб зберегти зміни.
            </div>
          )}
          {/* Тільки для адміна: технічний акаунт нічної докачки даних —
              паперовий, і шлюз має піднятись саме в paper-режимі. Звичайні
              юзери підключають бойовий акаунт, для них режим лишається 'live'
              без вибору. */}
          {isAdmin && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: C.textDim, display: 'block', marginBottom: 6 }}>
                Режим акаунта (видно лише адміну)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { key: 'live',  label: '💰 Live',  hint: 'бойовий' },
                  { key: 'paper', label: '📄 Paper', hint: 'паперовий' },
                ].map(m => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMode(m.key)}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${mode === m.key ? C.primary : C.border}`,
                      background: mode === m.key ? `${C.primary}12` : C.card,
                      color: C.textMain, fontSize: 13,
                      fontWeight: mode === m.key ? 600 : 400,
                    }}
                  >
                    {m.label} <span style={{ color: C.textDim, fontWeight: 400 }}>({m.hint})</span>
                  </button>
                ))}
              </div>
              <div style={{ color: C.textDim, marginTop: 6, fontSize: 12, lineHeight: '16px' }}>
                Режим визначає, у який шлюз IBKR логінитись. Paper-акаунт, збережений
                як Live, отримає запит 2FA і відмову брокера.
              </div>
            </div>
          )}
          <div style={{
            background: '#FFFBEB', border: '1px solid #FDE047', borderRadius: 8,
            padding: '10px 14px', fontSize: 12, color: '#92400E', marginBottom: 16,
          }}>
            Для Paper Trading: використовуйте окремий Paper Trading Username. Знайти його: Client Portal → Settings → Paper Trading Account.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={save} disabled={saving}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
                background: saving ? '#9CA3AF' : C.primary,
                color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
              }}
            >
              {saving ? '⏳ Збереження…' : 'Зберегти і підключити'}
            </button>
            <button
              onClick={() => { setShowForm(false); setMsg(null); }}
              style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, cursor: 'pointer', fontSize: 14, color: C.textMain }}
            >
              Скасувати
            </button>
          </div>
        </div>
      )}

      {/* Connected state (honest: real login confirmed, no pending 2FA) */}
      {connected ? (
        <div>
          <div style={{
            padding: '12px 16px', borderRadius: 12,
            background: '#F0FDF4', border: '1px solid #86EFAC',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 16,
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#15803D' }}>✅ Підключено</div>
              <div style={{ fontSize: 13, color: '#166534', marginTop: 2 }}>
                Account: {broker.account_id || '—'} · {broker.trading_mode === 'live' ? 'Live' : 'Paper'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={reconnect}
                disabled={reconnecting}
                title="Надіслати новий запит на вхід (2FA) — якщо зв'язок відпав або настав щотижневий повторний вхід"
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, fontSize: 13, cursor: reconnecting ? 'wait' : 'pointer', color: C.textMain }}
              >
                {reconnecting ? '⏳ Запит…' : '🔄 Перепідключити (2FA)'}
              </button>
              <button
                onClick={openForm}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, fontSize: 13, cursor: 'pointer', color: C.textMain }}
              >
                Змінити
              </button>
              <button
                onClick={disconnect}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(237,75,130,0.4)', background: C.card, fontSize: 13, cursor: 'pointer', color: C.danger }}
              >
                Відключити
              </button>
            </div>
          </div>

          {/* IBKR account type — read-only (set only in IBKR Client Portal) */}
          <div style={{
            padding: '12px 16px', borderRadius: 12,
            background: '#F8FAFC', border: `1px solid ${C.border}`,
            fontSize: 13, color: C.textMain,
          }}>
            {ibkrNetLiq > 0 && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${C.border}`,
              }}>
                <span style={{ color: C.textDim }}>Загальний баланс IBKR (весь рахунок)</span>
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  {ibkrNetLiq.toLocaleString('en-US', { maximumFractionDigits: 0 })} $
                </span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <span style={{ color: C.textDim }}>Тип акаунта: </span>
                <span style={{ fontWeight: 600 }}>
                  {accountType === 'margin' ? 'Маржинальний' : accountType === 'cash' ? 'Кеш (Cash)' : 'Визначається…'}
                </span>
                {accountType === 'cash' && unsettledCash > 0 && (
                  <span style={{ color: C.textDim }}>
                    {' · заморожено до T+1: '}
                    {unsettledCash.toLocaleString('en-US', { maximumFractionDigits: 0 })} $
                  </span>
                )}
              </div>
              <a
                href="https://www.interactivebrokers.com/sso/Login"
                target="_blank" rel="noopener noreferrer"
                style={{ color: '#475FFA', fontSize: 13, textDecoration: 'none', whiteSpace: 'nowrap' }}
              >
                Змінити тип у IBKR →
              </a>
            </div>
            <div style={{ color: C.textDim, marginTop: 6, fontSize: 12, lineHeight: '16px' }}>
              Тип акаунта задається в IBKR і змінюється лише в їхньому Client Portal, не з цього кабінету.
            </div>
          </div>

          {/* Cash-account notice: T+1 freeze → skipped buy signals + how to upgrade */}
          {accountType === 'cash' && (
            <div style={{
              padding: '12px 16px', borderRadius: 12,
              background: '#FFFBEB', border: '1px solid #FDE68A',
              fontSize: 13, color: C.textMain, lineHeight: '18px',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                ⚠️ Cash-акаунт: частина сигналів пропускається
              </div>
              <div style={{ marginBottom: 8 }}>
                Виручка від продажу акцій заморожується до наступного торгового дня (T+1).
                Поки кошти заморожені, робот пропускає сигнали купівлі — реальна дохідність
                може бути нижчою за розрахункову в бектесті.
              </div>
              <div style={{ color: C.textDim, fontSize: 12, lineHeight: '17px' }}>
                Щоб торгувати без заморозки, переведіть акаунт на маржинальний у кабінеті IBKR:{' '}
                <b>Settings → Account Settings → Account Type → Margin (Reg T)</b>.
                Вимоги IBKR: баланс від $2,000 та коротка анкета (фінансовий профіль і досвід
                торгівлі). Схвалення зазвичай до 1 робочого дня. Маржинальний акаунт сам по собі
                не змушує торгувати в кредит — робот використовує його лише для миттєвого
                реінвестування виручки.
              </div>
            </div>
          )}

          {/* Інфо: акції на рахунку + власні операції (ТЗ-21 (б), (в)) */}
          <div style={{
            padding: '12px 16px', borderRadius: 12,
            background: '#FFFBEB', border: '1px solid #FDE68A',
            fontSize: 12, color: C.textMain, lineHeight: '17px',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>ℹ️ Як робот працює з вашим акаунтом</div>
            <div style={{ marginBottom: 6 }}>
              <b>Акції на рахунку.</b> Якщо в акаунті є акції, куплені не роботом (були до
              підключення або ви докупили їх самостійно), алгоритм їх <b>не продає і не
              використовує</b>. Хочете, щоб алгоритм задіяв ці кошти — продайте такі акції
              самостійно в Interactive Brokers: робот побачить вільні кошти і почне з ними
              працювати. Робот торгує всіма вільними коштами акаунта та лише тими акціями,
              які купив сам.
            </div>
            <div>
              <b>Власні покупки та вивід коштів.</b> Щоб самостійно купити акції або вивести
              гроші — спершу <b>зупиніть робота</b>, зробіть операції в Interactive Brokers,
              потім запустіть робота знову. Інакше алгоритм одразу задіє вільні кошти в
              наступні покупки.
            </div>
          </div>

          {/* Marginable-only option (docs/MARGIN_2X_PLAN.md Phase 2).
              Лише адміну: юзери нічого з кредитом/marginable не обирають. */}
          {SHOW_PER_USER_MARGIN_CONTROLS && isAdmin && (
            <div style={{
              padding: '12px 16px', borderRadius: 12,
              background: '#F8FAFC', border: `1px solid ${C.border}`,
              fontSize: 13, color: C.textMain,
            }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={mgOnly}
                  disabled={mgSaving}
                  onChange={toggleMarginableOnly}
                  style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer' }}
                />
                <span>
                  <span style={{ fontWeight: 600 }}>
                    Купувати лише marginable акції
                  </span>
                  <span style={{ color: C.textDim }}> (на які брокер дає кредит)</span>
                  <div style={{ color: C.textDim, marginTop: 4, fontSize: 12, lineHeight: '16px' }}>
                    Робот пропускатиме сигнали купівлі по акціях без margin-кредиту IBKR
                    (OTC, низьколіквідні). Продажі відкритих позицій не обмежуються.
                    Зміна застосується після наступного запуску робота.
                    {' '}
                    <a
                      href="/marginality"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation();
                                        window.history.pushState({}, '', '/marginality');
                                        window.dispatchEvent(new PopStateEvent('popstate')); }}
                      style={{ color: C.accent || '#2563EB', textDecoration: 'underline' }}
                    >
                      Подивитись список акцій
                    </a>
                    {' — під які брокер дає кредит, а які не можна купити взагалі.'}
                  </div>
                </span>
              </label>
            </div>
          )}

          {/* Тижневе підтвердження входу IBKR: плановий запит + вікно повторів */}
          <div style={{
            padding: '12px 16px', borderRadius: 12,
            background: '#F8FAFC', border: `1px solid ${C.border}`,
            fontSize: 13, color: C.textMain,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Нагадувати про підтвердження входу лише у визначений час
            </div>
            <div style={{ color: C.textDim, fontSize: 12, lineHeight: '16px' }}>
              Раз на тиждень IBKR вимагає підтвердити вхід у застосунку на телефоні —
              до підтвердження торговий робот не працюватиме. Ми надсилаємо вам нагадування
              про це, а також ви отримаєте запит від застосунку IBKR на дозвіл підключення.
            </div>

            <div style={{ fontWeight: 500, marginTop: 14, marginBottom: 8 }}>
              Коли вам зручно отримувати цей запит
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: C.textDim, fontSize: 12 }}>День тижня</span>
              <select value={twofa.weekday} disabled={notifySaving}
                      onChange={(e) => saveTwofa({ weekday: Number(e.target.value) })}
                      style={SELECT_STYLE}>
                {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
              <span style={{ color: C.textDim, fontSize: 12, marginLeft: 6 }}>Година</span>
              <select value={twofa.hour} disabled={notifySaving}
                      onChange={(e) => saveTwofa({ hour: Number(e.target.value) })}
                      style={SELECT_STYLE}>
                {HOURS.map(h => <option key={h} value={h}>{pad2(h)}</option>)}
              </select>
              <span style={{ color: C.textDim, fontSize: 12 }}>Хвилини</span>
              <select value={twofa.minute} disabled={notifySaving}
                      onChange={(e) => saveTwofa({ minute: Number(e.target.value) })}
                      style={SELECT_STYLE}>
                {MINUTES.map(m => <option key={m} value={m}>{pad2(m)}</option>)}
              </select>
            </div>

            <div style={{ fontWeight: 500, marginTop: 14, marginBottom: 4 }}>
              Якщо ви не підтвердили запит
            </div>
            <div style={{ color: C.textDim, fontSize: 12, lineHeight: '16px', marginBottom: 8 }}>
              Задайте проміжок, у який вам зручно отримувати запит повторно — до моменту
              вашого підтвердження.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: C.textDim, fontSize: 12 }}>Від</span>
              <select value={twofa.start} disabled={notifySaving}
                      onChange={(e) => saveTwofa({ start: Number(e.target.value) })}
                      style={SELECT_STYLE}>
                {HOURS.map(h => <option key={h} value={h}>{pad2(h)}:00</option>)}
              </select>
              <span style={{ color: C.textDim, fontSize: 12 }}>до</span>
              <select value={twofa.end} disabled={notifySaving}
                      onChange={(e) => saveTwofa({ end: Number(e.target.value) })}
                      style={SELECT_STYLE}>
                {HOURS.map(h => <option key={h} value={h}>{pad2(h)}:00</option>)}
              </select>
            </div>
            <div style={{ color: C.textDim, fontSize: 12, lineHeight: '16px', marginTop: 8 }}>
              Час київський. Поки біржа працює, повторний запит надходить кожні пів години,
              коли біржа закрита — щогодини.
            </div>
          </div>

          {/* Leverage selector (без плеча / x1.1 … x2, admin ceiling) — margin accounts only, лише адміну */}
          {SHOW_PER_USER_MARGIN_CONTROLS && isAdmin && accountType === 'margin' && (
            <div style={{
              padding: '12px 16px', borderRadius: 12,
              background: '#F8FAFC', border: `1px solid ${C.border}`,
              fontSize: 13, color: C.textMain,
            }}>
              {(() => {
                const ceiling = Number(marginLeverageCeiling) || 2.0;
                const rungs = (Array.isArray(marginLeverageRungs) && marginLeverageRungs.length)
                  ? marginLeverageRungs
                  : [1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0].filter(r => r <= ceiling + 1e-9);
                const currentCap = lev ? (Number(marginLeverageCap) || ceiling) : 1.0;
                const options = [1.0, ...rungs];
                const fmt = (v) => (v <= 1.0 ? 'Без кредитного плеча' : `x${v}`);
                return (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Кредитне плече</div>
                    <div style={{ color: C.textDim, fontSize: 12, lineHeight: '16px', marginBottom: 10 }}>
                      Оберіть множник капіталу для розрахунку розміру позицій. Будь-яке значення від x1.1
                      вмикає маржинальну торгівлю (робот докуповує за кредит IBKR) і фільтр
                      marginable-акцій. Зміна застосується після наступного запуску робота.
                    </div>
                    {/* Випадний список: 11 варіантів кнопками не вміщались на телефоні. */}
                    <select
                      value={String(pendingCap !== null ? pendingCap : currentCap)}
                      disabled={levSaving}
                      onChange={e => onSelectCap(Number(e.target.value))}
                      style={{
                        padding: '8px 12px', borderRadius: 8, minWidth: 220,
                        border: `1px solid ${lev ? C.primary : C.border}`,
                        background: C.card, color: C.textMain, fontSize: 14,
                        cursor: levSaving ? 'wait' : 'pointer',
                      }}
                    >
                      {options.map(v => (
                        <option key={v} value={String(v)}>{fmt(v)}</option>
                      ))}
                    </select>
                    {pendingCap !== null && (
                      <div style={{
                        marginTop: 10, padding: '12px 14px', borderRadius: 10,
                        background: '#FEF2F2', border: '1px solid #FECACA',
                        fontSize: 12, lineHeight: '17px',
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>
                          ⚠️ Підвищений ризик — прочитайте перед увімкненням плеча x{pendingCap}
                        </div>
                        <ul style={{ margin: '0 0 8px 16px', padding: 0 }}>
                          <li>Ви торгуєте частково позиченими коштами: прибутки <b>і збитки множаться</b>.
                              При плечі x{pendingCap} просадка портфеля на 10% ≈ −{Math.round(pendingCap * 10)}% вашого капіталу.</li>
                          <li>При сильному падінні IBKR може <b>примусово продати</b> позиції (margin call).
                              Робот стежить за запасом і розвантажується раніше, але гарантій немає.</li>
                          <li>IBKR нараховує <b>~6% річних</b> на позичену суму — це зменшує прибуток
                              (сума відсотків показується у цій картці).</li>
                        </ul>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => saveCap(pendingCap)}
                            disabled={levSaving}
                            style={{
                              padding: '7px 12px', borderRadius: 8, border: 'none',
                              background: '#DC2626', color: '#fff', fontWeight: 600,
                              fontSize: 12, cursor: 'pointer',
                            }}
                          >
                            Розумію ризики, увімкнути x{pendingCap}
                          </button>
                          <button
                            onClick={() => setPendingCap(null)}
                            style={{
                              padding: '7px 12px', borderRadius: 8,
                              border: `1px solid ${C.border}`, background: '#fff',
                              color: C.textMain, fontSize: 12, cursor: 'pointer',
                            }}
                          >
                            Скасувати
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {(marginBorrowed > 0 || marginInterest > 0) && (
                <div style={{
                  marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`,
                  color: C.textDim, fontSize: 12, lineHeight: '17px',
                }}>
                  {marginBorrowed > 0 && (
                    <div>Позичено зараз: <b style={{ color: C.textMain }}>
                      {marginBorrowed.toLocaleString('en-US', { maximumFractionDigits: 0 })} $</b></div>
                  )}
                  {marginInterest > 0 && (
                    <div>Сплачено відсотків за кредит: <b style={{ color: C.textMain }}>
                      {marginInterest.toLocaleString('en-US', { maximumFractionDigits: 2 })} $</b></div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : linkLost ? (
        /* Звʼязок був і обірвався — окремо від «підключення…»: людині треба
           знати, що робот зараз НЕ торгує, з якого часу і чому. */
        <div>
          <div style={{
            padding: '12px 16px', borderRadius: 12,
            background: '#FEF2F2', border: '1px solid #FECACA',
            marginBottom: 16,
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#B91C1C' }}>
              ⚠️ Звʼязок з IBKR втрачено{link.since ? ` о ${new Date(link.since).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}` : ''}
            </div>
            <div style={{ fontSize: 13, color: '#991B1B', marginTop: 4, lineHeight: '18px' }}>
              Account: {broker.account_id || '—'} · {broker.trading_mode === 'live' ? 'Live' : 'Paper'}
              <br />
              {link.reason ? `Причина: ${link.reason}. ` : ''}
              Робот зараз не торгує. Він пробує відновити звʼязок сам; якщо не виходить — натисніть «Перепідключити» і підтвердіть вхід у застосунку IBKR.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={reconnect}
              disabled={reconnecting}
              title="Надіслати новий запит на вхід (2FA)"
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: C.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: reconnecting ? 'wait' : 'pointer' }}
            >
              {reconnecting ? '⏳ Запит…' : '🔄 Перепідключити (2FA)'}
            </button>
            <button
              onClick={openForm}
              style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, fontSize: 13, cursor: 'pointer', color: C.textMain }}
            >
              Змінити
            </button>
            <button
              onClick={disconnect}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(237,75,130,0.4)', background: C.card, fontSize: 13, cursor: 'pointer', color: C.danger }}
            >
              Відключити
            </button>
          </div>
        </div>
      ) : connecting ? (
        /* Credentials saved, but the gateway has NOT confirmed a real login yet
           (starting up, or awaiting the 2FA push). Show an honest "connecting"
           state — NOT "Підключено" — and keep Перепідключити available so the
           user can resend a hung 2FA push. */
        <div>
          <div style={{
            padding: '12px 16px', borderRadius: 12,
            background: '#FFFBEB', border: '1px solid #FDE68A',
            marginBottom: 16,
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#92400E' }}>
              ⏳ {awaiting2fa ? 'Очікування підтвердження входу (2FA)' : 'Підключення до IBKR…'}
            </div>
            <div style={{ fontSize: 13, color: '#92400E', marginTop: 4, lineHeight: '18px' }}>
              Account: {broker.account_id || '—'} · {broker.trading_mode === 'live' ? 'Live' : 'Paper'}
              <br />
              {awaiting2fa
                ? 'Підтвердіть вхід у застосунку IBKR (IB Key). Якщо пуш не прийшов — натисніть «Перепідключити», щоб надіслати новий.'
                : 'Триває вхід у IBKR. Це може зайняти до хвилини. Якщо потрібне підтвердження 2FA — воно прийде у застосунок IBKR. Якщо пуш завис — натисніть «Перепідключити».'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={reconnect}
              disabled={reconnecting}
              title="Надіслати новий запит на вхід (2FA)"
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: C.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: reconnecting ? 'wait' : 'pointer' }}
            >
              {reconnecting ? '⏳ Запит…' : '🔄 Перепідключити (2FA)'}
            </button>
            <button
              onClick={openForm}
              style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, fontSize: 13, cursor: 'pointer', color: C.textMain }}
            >
              Змінити
            </button>
            <button
              onClick={disconnect}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(237,75,130,0.4)', background: C.card, fontSize: 13, cursor: 'pointer', color: C.danger }}
            >
              Відключити
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => (showForm ? setShowForm(false) : openForm())}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 16px', borderRadius: 12, border: 'none',
            background: C.dark, color: '#fff',
            fontSize: 16, fontWeight: 500, cursor: 'pointer',
            boxShadow: '0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)',
          }}
        >
          <IBKRIcon />
          <span>Підключити IBKR →</span>
        </button>
      )}

    </div>
  );
}

/* ── Bot control card ─────────────────────────────────────────────── */
function BotControlCard({ containers, activeStrategy, botAction, capital, onCapitalChange, onStart, onStop, onRefresh, paused }) {
  const bot   = containers?.bot  || null;
  // «Зупинити» = пауза: контейнер працює, але торгівлю вимкнено
  // (trading_mode='stopped'). «Запустити» знімає паузу без 2FA.
  const isPaused   = bot?.status === 'running' && !!paused;
  const isRunning  = bot?.status === 'running' && !paused;
  const isStarting = bot?.status === 'starting';
  const isStopped  = !bot || bot.status === 'stopped';
  const isError    = bot?.status === 'error';
  const canStart   = isStopped || isError || isPaused;

  return (
    <div style={{
      background: C.card,
      border: `0.667px solid ${C.border}`,
      borderRadius: 20,
      padding: 24,
      boxShadow: C.shadow,
    }}>
      <div style={{ fontSize: 20, fontWeight: 600, color: C.textMain, marginBottom: 16, lineHeight: '28px' }}>
        🤖 Торговий робот
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        {/* Status indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            {isRunning  && <span style={{ color: '#15803D' }}>🟢 Працює</span>}
            {isStarting && <span style={{ color: '#D97706' }}>🟡 Запускається...</span>}
            {isError    && <span style={{ color: '#DC2626' }}>🔴 Помилка</span>}
            {isPaused   && <span style={{ color: '#D97706' }}>⏸ Зупинено (пауза)</span>}
            {canStart && !isPaused && <span style={{ color: '#94A3B8' }}>⚫ Зупинено</span>}
          </span>
          {isRunning && activeStrategy && (
            <span style={{ fontSize: 13, color: C.textDim }}>
              · {activeStrategy.display_name || activeStrategy.name}
            </span>
          )}
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {canStart && (
            /* ТЗ-21 Q1=А: поле суми прибрано — бот торгує всім вільним кешем
               акаунта IBKR (капітал більше не задається вручну). */
            <span style={{ fontSize: 12, color: C.textDim, maxWidth: 260, lineHeight: '15px' }}>
              Робот використовує весь вільний кеш вашого акаунта IBKR
            </span>
          )}
          {canStart && (
            <button
              onClick={onStart}
              disabled={!!botAction}
              style={{
                padding: '9px 20px', borderRadius: 10, border: 'none',
                background: botAction ? '#94A3B8' : '#22C55E',
                color: '#fff', fontWeight: 700, fontSize: 14,
                cursor: botAction ? 'wait' : 'pointer',
              }}
            >
              {botAction === 'starting' ? '⏳ Запускається...' : '▶ Запустити робота'}
            </button>
          )}
          {isRunning && (
            <button
              onClick={onStop}
              disabled={!!botAction}
              style={{
                padding: '9px 20px', borderRadius: 10, border: 'none',
                background: botAction ? '#94A3B8' : '#EF4444',
                color: '#fff', fontWeight: 700, fontSize: 14,
                cursor: botAction ? 'wait' : 'pointer',
              }}
            >
              {botAction === 'stopping' ? '⏳ Зупиняється...' : '⏹ Зупинити робота'}
            </button>
          )}
          <button
            onClick={onRefresh}
            style={{
              padding: '9px 12px', borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: 'transparent', cursor: 'pointer', fontSize: 16,
            }}
            title="Оновити статус"
          >
            ↻
          </button>
        </div>
      </div>

      {isError && bot?.error_msg && (
        <div style={{
          marginTop: 12, fontSize: 12, color: '#DC2626',
          background: '#FEF2F2', borderRadius: 8, padding: '8px 12px',
        }}>
          {bot.error_msg}
        </div>
      )}
    </div>
  );
}

/* ── Virtual session mini-card ────────────────────────────────────── */
function VirtualSectionCard({ navigateTo }) {
  const [vsState,   setVsState]   = useState('loading'); // 'loading'|'has'|'none'
  const [session,   setSession]   = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  const reloadVs = () => {
    setVsState('loading');
    getVirtualSession()
      .then(r => {
        if (r?.data?.session) { setSession(r.data.session); setVsState('has'); }
        else setVsState('none');
      })
      .catch(() => setVsState('none'));
  };

  useEffect(() => { reloadVs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (vsState === 'loading') return null;

  /* User already has a session — show summary + actions */
  if (vsState === 'has' && session) {
    const rj = session.result_json || {};
    const profit = rj.total_return_pct;
    const balance = rj.final_balance ?? session.amount;
    const profitColor = profit == null ? '#5B646F' : profit >= 0 ? '#00A656' : '#FB2C36';
    const startDate = session.start_date
      ? new Date(session.start_date).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '—';

    const handleDelete = async () => {
      if (!window.confirm('Видалити поточну сесію? Дані будуть втрачені.')) return;
      setDeleting(true);
      try {
        await deleteVirtualSession();
        setSession(null);
        setVsState('none');
      } catch { /* ignore */ }
      setDeleting(false);
    };

    return (
      <div style={{
        background: C.card,
        border: `0.667px solid ${C.border}`,
        borderRadius: 20,
        padding: 24,
        boxShadow: C.shadow,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 600, color: C.textMain, lineHeight: '28px' }}>
              Мій портфель (віртуальний)
            </div>
            <div style={{ fontSize: 14, color: C.textDim, marginTop: 4 }}>
              Активна симуляція з {startDate}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {navigateTo && (
              <button
                onClick={() => navigateTo('/')}
                style={{
                  padding: '10px 20px', borderRadius: 12, border: 'none',
                  background: C.primary, color: '#fff',
                  fontSize: 14, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Переглянути →
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                padding: '10px 16px', borderRadius: 12,
                border: `1px solid rgba(237,75,130,0.35)`,
                background: C.card, color: C.danger,
                fontSize: 14, fontWeight: 500, cursor: deleting ? 'wait' : 'pointer',
              }}
            >
              {deleting ? 'Видалення…' : 'Видалити'}
            </button>
          </div>
        </div>

        {balance != null && (
          <div style={{ display: 'flex', gap: 24, marginTop: 20 }}>
            <div>
              <div style={{ fontSize: 12, color: C.textDim, textTransform: 'uppercase', letterSpacing: 0.5 }}>Баланс</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: C.textMain, lineHeight: '1.3em' }}>
                {Number(balance).toLocaleString('en-US', { maximumFractionDigits: 0 })} $
              </div>
            </div>
            {profit != null && (
              <div>
                <div style={{ fontSize: 12, color: C.textDim, textTransform: 'uppercase', letterSpacing: 0.5 }}>Дохідність</div>
                <div style={{ fontSize: 24, fontWeight: 600, color: profitColor, lineHeight: '1.3em' }}>
                  {(profit >= 0 ? '+' : '') + profit.toFixed(1) + '%'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  /* No session — show compact start form inline */
  return <VirtualStartCard onSessionCreated={reloadVs} />;
}

/* ── Compact virtual start card (self-contained form) ─────────────── */
const AMOUNT_PRESETS = [1000, 5000, 10000, 25000, 50000, 100000];

function VirtualStartCard({ onSessionCreated }) {
  const [amount,   setAmount]   = useState(10000);
  const [reinvest, setReinvest] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error,    setError]    = useState('');

  const handleStart = async () => {
    setStarting(true); setError('');
    try {
      await createVirtualSession({
        start_date: new Date().toISOString().slice(0, 10),
        amount: Number(amount) || 10000,
        reinvest,
      });
      onSessionCreated();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      // 409 = session already exists (e.g. first request created it but client timed out)
      // Network error = same scenario possible → check if session appeared
      if (status === 409 || !e?.response) {
        try {
          const check = await getVirtualSession();
          if (check?.data?.session) { onSessionCreated(); return; }
        } catch { /* ignore */ }
      }

      setError(typeof detail === 'string' ? detail : 'Помилка запуску');
      setStarting(false);
    }
  };

  return (
    <div style={{
      background: 'radial-gradient(circle at 80% 130%, #0F153C 0%, #1F3B87 40%, #2D4893 100%)',
      borderRadius: 20,
      padding: 28,
      boxShadow: C.shadow,
      display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap',
    }}>
      {/* Left dark panel */}
      <div style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{
          fontSize: 22, fontWeight: 600, lineHeight: '1.25em',
          background: 'linear-gradient(90deg, #fff 0%, #83FFC1 83%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          Почати безкоштовні віртуальні торги
        </div>
        <p style={{ margin: 0, fontSize: 14, color: '#EFE6FD', lineHeight: '1.5em' }}>
          Введіть суму — алгоритм автоматично починає торгувати акціями. Без ризику для реальних коштів.
        </p>
      </div>

      {/* Right white card */}
      <div style={{
        flex: '1 1 320px', minWidth: 280,
        background: '#fff', borderRadius: 16, padding: 24,
        display: 'flex', flexDirection: 'column', gap: 18,
      }}>
        {/* Presets */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 10 }}>Ввести суму ($)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {AMOUNT_PRESETS.map(amt => (
              <button
                key={amt}
                onClick={() => setAmount(amt)}
                style={{
                  padding: '8px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
                  background: amount === amt ? '#475FFA' : '#ECF3FF',
                  color: amount === amt ? '#fff' : '#1C222B',
                  fontSize: 13, fontWeight: 500,
                }}
              >
                {amt.toLocaleString('en-US')} $
              </button>
            ))}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', height: 44,
            padding: '0 12px', borderRadius: 10,
            border: '1px solid #E0E5EB', background: '#fff',
          }}>
            <input
              type="number" min={100} step={100} value={amount}
              onChange={e => setAmount(Math.max(100, Number(e.target.value) || 100))}
              placeholder="Своя сума"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 15, fontWeight: 500, color: '#0F171F' }}
            />
            <span style={{ fontSize: 15, fontWeight: 500, color: '#0F171F' }}>$</span>
          </div>
        </div>

        {/* Reinvest checkbox */}
        <button
          type="button"
          onClick={() => setReinvest(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 18, height: 18, borderRadius: 4, flexShrink: 0,
            background: reinvest ? '#1C69E3' : '#fff',
            border: `2px solid ${reinvest ? '#1C69E3' : '#9CA3AF'}`,
          }}>
            {reinvest && (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M5 12l4 4 10-10" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>Реінвестувати прибуток</span>
        </button>

        {error && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(251,44,54,0.08)', color: '#FB2C36', fontSize: 13 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleStart}
          disabled={starting || amount < 100}
          style={{
            height: 46, borderRadius: 10, border: 'none',
            background: starting ? '#9CA3AF' : '#475FFA',
            color: '#fff', fontSize: 15, fontWeight: 500, cursor: starting ? 'wait' : 'pointer',
          }}
        >
          {starting ? 'Запуск…' : 'Почати віртуальні торги'}
        </button>
      </div>
    </div>
  );
}

/* ── Root ──────────────────────────────────────────────────────────── */
function ReportSettingsCard() {
  const [s, setS]       = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    getReportSettings().then(r => setS(r.data)).catch(() => setS(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (patch) => {
    setSaving(true);
    try { await updateReportSettings(patch); await load(); } finally { setSaving(false); }
  };
  if (!s) return null;
  const Box = ({ on }) => (on ? <RadioChecked /> : <RadioUnchecked />);
  const Tick = ({ on }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 18, height: 18, borderRadius: 4, flexShrink: 0,
      background: on ? '#1C69E3' : '#fff',
      border: `2px solid ${on ? '#1C69E3' : '#9CA3AF'}`,
    }}>
      {on && (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
          <path d="M5 12l4 4 10-10" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E9F0', borderRadius: 16, padding: 24 }}>
      <div style={{ fontWeight: 700, fontSize: 16, color: C.textMain, marginBottom: 4 }}>
        Нагадування про підтвердження входу до IBKR
      </div>
      <div style={{ fontSize: 13, color: C.textDim, marginBottom: 16 }}>
        Раз на тиждень, у час, який ви обрали в картці Interactive Brokers
      </div>
      <label onClick={() => !saving && save({ twofa_email_enabled: !s.twofa_email_enabled })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
        <Box on={s.twofa_email_enabled} /><span style={{ color: C.textMain }}>По Email</span>
      </label>
      <label onClick={() => { if (!saving && s.telegram_linked) save({ twofa_telegram_enabled: !s.twofa_telegram_enabled }); }}
             style={{ display: 'flex', alignItems: 'center', gap: 10,
                      cursor: s.telegram_linked ? 'pointer' : 'default' }}>
        <Box on={s.twofa_telegram_enabled} /><span style={{ color: C.textMain }}>По Телеграму</span>
        {s.telegram_linked
          ? <span style={{ marginLeft: 8, fontSize: 12, color: '#15803D', fontWeight: 600 }}>✅ підключено</span>
          : (s.telegram_link && (
              <a href={s.telegram_link} target="_blank" rel="noreferrer"
                 onClick={(e) => e.stopPropagation()}
                 style={{ marginLeft: 8, fontSize: 13, color: '#2563EB', fontWeight: 600 }}>
                Підключити Telegram →
              </a>))}
      </label>
      <label onClick={() => !saving && save({ twofa_email_enabled: false, twofa_telegram_enabled: false })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 12 }}>
        <Box on={!s.twofa_email_enabled && !s.twofa_telegram_enabled} /><span style={{ color: C.textMain }}>Не інформувати</span>
      </label>

      <div style={{ height: 1, background: '#EEF1F6', margin: '18px 0' }} />
      <div style={{ fontWeight: 700, fontSize: 15, color: C.textMain, marginBottom: 16 }}>
        Повідомлення про покупки\продажі
      </div>
      <label onClick={() => !saving && save({ trade_notify_buy: !s.trade_notify_buy })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
        <Tick on={s.trade_notify_buy} /><span style={{ color: C.textMain }}>Інформувати при покупці</span>
      </label>
      <label onClick={() => !saving && save({ trade_notify_sell: !s.trade_notify_sell })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
        <Tick on={s.trade_notify_sell} /><span style={{ color: C.textMain }}>Інформувати при продажі</span>
      </label>
      <label onClick={() => { if (!saving && s.trade_notify_sell) save({ trade_notify_profit_only: !s.trade_notify_profit_only }); }}
             style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, marginLeft: 28,
                      cursor: s.trade_notify_sell ? 'pointer' : 'default',
                      opacity: s.trade_notify_sell ? 1 : 0.45 }}>
        <Tick on={s.trade_notify_profit_only} /><span style={{ color: C.textMain }}>Тільки про прибуткові продажі</span>
      </label>
      <label onClick={() => !saving && save({ trade_email_enabled: !s.trade_email_enabled })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
        <Box on={s.trade_email_enabled} /><span style={{ color: C.textMain }}>По Email</span>
      </label>
      <label onClick={() => { if (!saving && s.telegram_linked) save({ trade_telegram_enabled: !s.trade_telegram_enabled }); }}
             style={{ display: 'flex', alignItems: 'center', gap: 10,
                      cursor: s.telegram_linked ? 'pointer' : 'default' }}>
        <Box on={s.trade_telegram_enabled} /><span style={{ color: C.textMain }}>По Телеграму</span>
        {s.telegram_linked
          ? <span style={{ marginLeft: 8, fontSize: 12, color: '#15803D', fontWeight: 600 }}>✅ підключено</span>
          : (s.telegram_link && (
              <a href={s.telegram_link} target="_blank" rel="noreferrer"
                 onClick={(e) => e.stopPropagation()}
                 style={{ marginLeft: 8, fontSize: 13, color: '#2563EB', fontWeight: 600 }}>
                Підключити Telegram →
              </a>))}
      </label>
      <label onClick={() => !saving && save({ trade_email_enabled: false, trade_telegram_enabled: false })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 12 }}>
        <Box on={!s.trade_email_enabled && !s.trade_telegram_enabled} /><span style={{ color: C.textMain }}>Не інформувати</span>
      </label>

      <div style={{ height: 1, background: '#EEF1F6', margin: '18px 0' }} />
      <div style={{ fontWeight: 700, fontSize: 15, color: C.textMain, marginBottom: 4 }}>
        Звіти про дохідність
      </div>
      <div style={{ fontSize: 13, color: C.textDim, marginBottom: 16 }}>
        Інформувати мене про приріст капіталу {s.frequency_label || 'раз на три місяці'}
      </div>
      <label onClick={() => !saving && save({ email_enabled: !s.email_enabled })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
        <Box on={s.email_enabled} /><span style={{ color: C.textMain }}>По Email</span>
      </label>
      <label onClick={() => { if (!saving && s.telegram_linked) save({ telegram_enabled: !s.telegram_enabled }); }}
             style={{ display: 'flex', alignItems: 'center', gap: 10,
                      cursor: s.telegram_linked ? 'pointer' : 'default' }}>
        <Box on={s.telegram_enabled} /><span style={{ color: C.textMain }}>По Телеграму</span>
        {s.telegram_linked
          ? <span style={{ marginLeft: 8, fontSize: 12, color: '#15803D', fontWeight: 600 }}>✅ підключено</span>
          : (s.telegram_link && (
              <a href={s.telegram_link} target="_blank" rel="noreferrer"
                 onClick={(e) => e.stopPropagation()}
                 style={{ marginLeft: 8, fontSize: 13, color: '#2563EB', fontWeight: 600 }}>
                Підключити Telegram →
              </a>))}
      </label>
      <label onClick={() => !saving && save({ email_enabled: false, telegram_enabled: false })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 12 }}>
        <Box on={!s.email_enabled && !s.telegram_enabled} /><span style={{ color: C.textMain }}>Не інформувати</span>
      </label>

      <div style={{ height: 1, background: '#EEF1F6', margin: '18px 0' }} />
      <div style={{ fontWeight: 700, fontSize: 15, color: C.textMain, marginBottom: 4 }}>
        Новини та оголошення
      </div>
      <div style={{ fontSize: 13, color: C.textDim, marginBottom: 16 }}>
        Отримувати важливі оновлення та новини сервісу
      </div>
      <label onClick={() => !saving && save({ news_email_enabled: !s.news_email_enabled })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
        <Box on={s.news_email_enabled} /><span style={{ color: C.textMain }}>По Email</span>
      </label>
      <label onClick={() => { if (!saving && s.telegram_linked) save({ news_telegram_enabled: !s.news_telegram_enabled }); }}
             style={{ display: 'flex', alignItems: 'center', gap: 10,
                      cursor: s.telegram_linked ? 'pointer' : 'default' }}>
        <Box on={s.news_telegram_enabled} /><span style={{ color: C.textMain }}>По Телеграму</span>
        {!s.telegram_linked && <span style={{ marginLeft: 8, fontSize: 12, color: C.textDim }}>(підключіть вище)</span>}
      </label>
      <label onClick={() => !saving && save({ news_email_enabled: false, news_telegram_enabled: false })}
             style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 12 }}>
        <Box on={!s.news_email_enabled && !s.news_telegram_enabled} /><span style={{ color: C.textMain }}>Не інформувати</span>
      </label>
    </div>
  );
}

export default function TradingAccountPage({ navigateTo }) {
  const [profile,        setProfile]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [containers,     setContainers]     = useState(null);
  const [activeStrategy, setActiveStrategy] = useState(null);
  const [botAction,      setBotAction]      = useState(null); // 'starting'|'stopping'|null
  const [capital,        setCapital]        = useState(100000);
  const [portfolio,      setPortfolio]      = useState(null);  // /api/portfolio: real P&L + account_type
  const [needs2fa,       setNeeds2fa]       = useState(false); // /api/status: watchdog 2FA-pending signal

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadProfile = async () => {
    try {
      const r = await fetch(`${BASE}/api/user/profile`, { headers });
      const d = await r.json();
      setProfile(d);
    } catch { /* silent */ }
    setLoading(false);
  };

  const refreshStatus = useCallback(async () => {
    try {
      const r = await getStatus();
      if (r?.data?.containers) setContainers(r.data.containers);
      if (r?.data?.capital != null) setCapital(r.data.capital);
      if (r?.data) setNeeds2fa(!!r.data.needs_2fa);
    } catch { /* silent */ }
  }, []);

  const loadPortfolio = useCallback(() => {
    getPortfolio().then(r => setPortfolio(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadProfile();
    refreshStatus();
    loadPortfolio();
    getActiveStrategyPublic().then(r => setActiveStrategy(r.data)).catch(() => {});
    // Poll profile too — broker connection state (is_verified / "Підключено")
    // lives on /api/user/profile and flips ASYNCHRONOUSLY after the user approves
    // 2FA inside the gateway. Without re-loading the profile here the cabinet would
    // keep showing "Підключення…" until a manual page refresh.
    const iv = setInterval(() => { loadProfile(); refreshStatus(); loadPortfolio(); }, 10000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startBot = async () => {
    setBotAction('starting');
    try {
      const connType  = broker?.trading_mode === 'live' ? 'live' : 'paper';
      const stratName = activeStrategy?.name || 'meanreversion';
      await fetch(`${BASE}/api/bot/start`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ strategy_name: stratName, capital: Number(capital) || 100000, connector_type: connType }),
      });
      await refreshStatus();
      await loadProfile();   // пауза/відновлення — це settings.trading_mode
    } catch (e) { alert('Помилка запуску: ' + e.message); }
    setBotAction(null);
  };

  const stopBot = async () => {
    setBotAction('stopping');
    try {
      await fetch(`${BASE}/api/bot/stop`, { method: 'POST', headers });
      await refreshStatus();
      await loadProfile();
    } catch (e) { alert('Помилка зупинки: ' + e.message); }
    setBotAction(null);
  };

  if (loading) {
    return (
      <div style={{ padding: 40, color: C.textDim, fontFamily: "'Inter',sans-serif" }}>
        Завантаження…
      </div>
    );
  }

  const user     = profile?.user;
  const balance  = profile?.balance;
  const broker   = profile?.broker;
  const settings = profile?.settings;

  // IBKR really connected (real login confirmed, no pending 2FA) — mirrors the
  // IbkrCard logic. Gates the "Start bot" card so a bot can't be launched before
  // the gateway has actually authenticated. A running bot always keeps its
  // controls (to Stop), even mid weekly-2FA.
  const _gwRunning  = containers?.ibgw?.status === 'running';
  const _botRunning = containers?.bot?.status === 'running';
  // is_verified (probe-confirmed real login) is the truth; needs2fa is a noisy
  // hint and must NOT block a confirmed connection (it can stick true post-login).
  const ibkrConnected = !!(broker && broker.ibkr_login)
                        && ((!!broker.is_verified && _gwRunning) || _botRunning);
  const showBotControl = _botRunning || ibkrConnected;

  // Bot balance from /api/portfolio (same source as P&L) — balance_snapshots is
  // often empty, so profile.balance.total shows "—" while portfolio.total_value is live.
  // Use /api/portfolio's total_value ONLY once the bot has published a real
  // snapshot (has_snapshot). Before that the backend returns the _INITIAL_CAPITAL
  // fallback ($100k), so showing it would blink a phantom balance before the real
  // IBKR account balance arrives — fall through to "—/Завантаження…" instead.
  const portfolioBalance = portfolio?.has_snapshot ? portfolio.total_value : null;
  const totalBalance = portfolioBalance ?? balance?.total ?? null;
  const tradingMode  = settings?.trading_mode || 'normal';
  const isActive     = settings?.is_active;

  /* Real P&L from /api/portfolio (total value − allocated capital). Was a hardcoded mock. */
  const pnlUsd   = portfolio?.total_pnl_usd;
  // Gate on has_snapshot too — before the first real snapshot total_pnl_usd is
  // derived from the phantom $100k fallback (→ a misleading 0 $ / 0%).
  const pnlKnown = broker?.is_verified && portfolio?.has_snapshot && pnlUsd != null;
  const pnlText  = pnlKnown
    ? (pnlUsd >= 0 ? '+' : '') + pnlUsd.toLocaleString('en-US', { maximumFractionDigits: 0 }) + ' $'
    : '—';
  const pnlColor = pnlKnown ? (pnlUsd >= 0 ? '#00A656' : '#DC2626') : undefined;
  /* IBKR account type (margin | cash | null) + funds frozen until T+1 — from /api/portfolio. */
  const accountType   = portfolio?.account_type || null;
  const unsettledCash = portfolio?.unsettled_cash || 0;
  const statusLabel = !_botRunning ? 'Не активно'
                    : tradingMode === 'stopped' ? 'Пауза' : (isActive === false ? 'Не активно' : 'Активно');

  return (
    <div style={{
      background: C.bg, minHeight: '100vh', padding: '24px 0',
      fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>

        {/* ── 3 KPI stat cards ────────────────────────────────────── */}
        {SHOW_ACCOUNT_KPI_CARDS && <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <StatCard
              label="Загальний баланс"
              value={totalBalance != null
                ? totalBalance.toLocaleString('en-US', { maximumFractionDigits: 0 }) + ' $'
                : (ibkrConnected ? 'Завантаження…' : '—')}
              iconBg="rgba(28,105,227,0.1)"
              icon={<WalletIcon />}
              sub="Поточна вартість"
            />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <StatCard
              label="Прибуток / збиток (P&L)"
              value={pnlText}
              valueColor={pnlColor}
              iconBg="rgba(0,166,86,0.1)"
              icon={<TrendIcon />}
            />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <StatCard
              label="Статус торгів"
              value={statusLabel}
              iconBg="rgba(151,153,164,0.15)"
              icon={<ShieldIcon />}
            />
          </div>
        </div>}

        {/* ── Main content ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Virtual trading section — only when NO IBKR credentials saved yet.
              (`broker` is non-null exactly when a broker_keys row exists, i.e.
              credentials were saved — the row is created only by save.) */}
          {!broker && (
            <VirtualSectionCard navigateTo={navigateTo} />
          )}

          {/* IBKR */}
          <IbkrCard broker={broker} onRefresh={loadProfile} accountType={accountType} unsettledCash={unsettledCash} ibkrNetLiq={portfolio?.ibkr_net_liq} marginableOnly={settings?.marginable_only} marginLeverage={settings?.margin_leverage} marginLeverageCap={settings?.margin_leverage_cap} marginLeverageCeiling={settings?.margin_leverage_ceiling} marginLeverageRungs={settings?.margin_leverage_rungs} marginBorrowed={portfolio?.margin_borrowed || 0} marginInterest={portfolio?.margin_interest_total || 0} twofaNotifyStartHour={settings?.twofa_notify_start_hour} twofaNotifyEndHour={settings?.twofa_notify_end_hour} twofaReloginWeekday={settings?.twofa_relogin_weekday} twofaReloginHour={settings?.twofa_relogin_hour} twofaReloginMinute={settings?.twofa_relogin_minute} needs2fa={needs2fa} containers={containers} isAdmin={user?.role === 'admin'} roleKnown={!!user?.role} />

          {/* Bot control — show ONLY once IBKR is REALLY connected (real login
              confirmed, no pending 2FA), so a bot can't be started before the
              gateway authenticates. A running bot always keeps its controls (to
              Stop). Connection status is polled every 10s by refreshStatus. */}
          {showBotControl && (
            <BotControlCard
              containers={containers}
              activeStrategy={activeStrategy}
              botAction={botAction}
              capital={capital}
              onCapitalChange={setCapital}
              onStart={startBot}
              onStop={stopBot}
              onRefresh={refreshStatus}
              paused={tradingMode === 'stopped'}
            />
          )}


          {/* Мої дані + пароль: на десктопі в один рядок, на телефоні одне під одним */}
          <div style={{
            display: 'grid', gap: 20, alignItems: 'stretch',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))',
          }}>
            {/* Profile data (name / email / phone / avatar) */}
            <ProfileCard user={user} onSaved={loadProfile} />

            {/* Password change */}
            <PasswordCard user={user} onSaved={loadProfile} />
          </div>

          {/* Report subscription (email / telegram) */}
          <ReportSettingsCard />

        </div>
      </div>
    </div>
  );
}
